<template>
	<div class="enable-multi-factor social-login__form">
		<div v-if="!CognitoConfiguration.NewLoginEnabled" class="sz-2 bg-info border border-info p-3 mb-2">
			<p>{{ organization }} requires two-factor authentication.</p>
		</div>
		<c-callout v-else type="info">
			<p>{{ organization }} requires two-factor authentication.</p>
		</c-callout>
		<div class="enable-multi-factor__container">
			<c-multifactor-secret v-if="secret" :secret="secret" :setup-url="setupUrl" />
			<c-multifactor-input :is-validated="isCodeValidated" :is-loading="isValidatingInput" @complete="validateCode" />
		</div>
		<ValidationObserver
			ref="observer"
			tag="form"
			novalidate
			class="cog-form"
			data-vv-scope="observer"
			@submit.prevent="handleEnableMfa"
		>
			<c-validated-input
				id="email-address"
				ref="email"
				v-model.trim="backupEmailAddress"
				vid="email-address"
				type="email"
				name="Backup Email Address"
				placeholder="Backup Email Address"
				autocomplete="on"
				rules="required|email|is-not-same-email"
				class="mt-4 mb-2"
			/>
			<div class="helptext text-1 mb-2">This backup email will be used to send authentication codes when you cannot access your authenticator app.</div>
			<div class="dialog__footer flex flex--sb flex--end">
				<c-status-button class="button button--primary text-4 my-3" :status="status" type="submit">
					Enable
				</c-status-button>
			</div>
		</ValidationObserver>
	</div>
</template>

<script lang="ts">
	import CMultifactorSecret from 'src/components/form-controls/MultifactorSecret.vue';
	import CMultifactorInput from 'src/components/form-controls/MultifactorInput.vue';
	import { initiatePublicMfaSetup, validateTempOtp, enableMfa } from '@cognitoforms/api/services/settings/account-service';
	import { enableMultifactor, initEnableMultifactor } from '@cognitoforms/api/services/auth-service';
	import { multifactorLogin } from '@cognitoforms/api/services/authentication';
	import { ValidationObserver, extend } from 'vee-validate';
	import type { ButtonStatus } from '@cognitoforms/shared-components/src/components/interaction/StatusButton.vue';
	import CStatusButton from '@cognitoforms/shared-components/src/components/interaction/StatusButton.vue';
	import CValidatedInput from '@cognitoforms/shared-components/src/components/form-controls/ValidatedInput.vue';
	import CCallout from '@cognitoforms/shared-components/src/components/content/Callout.vue';
	import notify from '@cognitoforms/shared-components/src/utilities/notify';
	import { computed, defineComponent, ref, watch } from 'vue';
	import { CognitoConfiguration } from '@cognitoforms/utils/clientside-configuration';
	import { useRoute } from 'vue-router/composables';

	export default defineComponent({
		components: {
			CMultifactorSecret,
			CMultifactorInput,
			ValidationObserver,
			CStatusButton,
			CValidatedInput,
			CCallout
		},
		props: {
			token: { type: String, default: '' },
			rememberMe: { type: Boolean, default: false },
			encryptedAuthMethod: { type: String, default: null }
		},
		emits: ['title-changed'],
		setup(props, { emit }) {
			const secret = ref<string>('');
			const setupUrl = ref<string>('');
			const password = ref<string>('');
			const isCodeValidated = ref<boolean>(false);
			const isLoading = ref<boolean>(false);
			const isValidatingInput = ref<boolean>(false);

			const emailAddress = ref<string>(null);
			const otp = ref<string>('');

			const organization = ref<string>('');

			const hasError = ref<boolean>(false);
			const backupEmailAddress = ref<string>('');
			watch(backupEmailAddress, (newEmail) => {
				if (newEmail)
					hasError.value = false;
			});

			const status = computed<ButtonStatus>(() => {
				if (isLoading.value)
					return 'validating';
				else if (!isCodeValidated.value || !backupEmailAddress.value || emailAddress.value === backupEmailAddress.value)
					return 'disabled';
				else if (hasError.value)
					return 'error';
				return 'default';
			});

			const validateCode = (code: string) => {
				isValidatingInput.value = true;
				validateTempOtp(secret.value, code).then(res => {
					isValidatingInput.value = false;
					isCodeValidated.value = res;
					otp.value = code;
				});
			};

			const mfaLogin = (code: string) => {
				isLoading.value = true;
				multifactorLogin(props.token, code, props.rememberMe, null, null, null, null, null, props.encryptedAuthMethod).then(res => {
					if (res.Succeeded) {
						setTimeout(() => {
							return window.location.assign(res.ReturnUrl);
						}, 500);
					}
					else {
						isLoading.value = false;
					}
				}).catch(() => {
					isLoading.value = false;
				});
			};

			const route = useRoute();
			const observer = ref<typeof ValidationObserver>(null);
			const handleEnableMfa = () => {
				const handleEnableError = (err) => {
					if (err.statusCode === 400) {
						if (err.message.toLowerCase().includes('email')) {
							observer.value.setErrors({ 'email-address': err.message });
							hasError.value = true;
						}
						else if (err.message.toLowerCase() === 'session is null.')
							window.location.assign('/login');
					}
					else
						notify('An error has occurred', 'error');
				};

				if (CognitoConfiguration.NewLoginEnabled) {
					enableMultifactor(backupEmailAddress.value, secret.value, otp.value, route.query.organizationCode as string).then(() => {
						window.location.assign(route.query.returnUrl as string);
					}) .catch(err => handleEnableError(err));
				}
				else {
					enableMfa(secret.value, backupEmailAddress.value, otp.value, props.token).then(() => {
						mfaLogin(otp.value);
					}).catch(err => handleEnableError(err));
				}
			};

			extend('is-not-same-email', {
				validate: value => {
					return emailAddress.value !== value;
				},
				message: 'Your backup email address must be different from your account email address.'
			});

			if (!CognitoConfiguration.NewLoginEnabled) {
				initiatePublicMfaSetup(props.token).then(res => {
					secret.value = res.SetupCode.Secret;
					setupUrl.value = res.SetupCode.SetupUrl;
					emailAddress.value = res.UserEmail;
					organization.value = res.OrganizationName;
				});
			}
			else {
				emit('title-changed', 'Two-Factor Authentication');
				organization.value = route.query.desiredOrganization as string;

				initEnableMultifactor().then(res => {
					secret.value = res.Secret;
					setupUrl.value = res.SetupUrl;
					emailAddress.value = res.UserEmail;
				});
			}
			return {
				secret,
				setupUrl,
				backupEmailAddress,
				password,
				isCodeValidated,
				isLoading,
				isValidatingInput,
				otp,
				organization,
				hasError,
				status,
				validateCode,
				handleEnableMfa,
				CognitoConfiguration
			};
		}
	});
</script>

<style lang="scss">
.enable-multi-factor {

	&__container {
		border: 1px solid var(--border-subtle);
		padding: 1rem 1.75rem;

		a {
			color: var(--text-link);
		}
	}
}

[data-auth-style='app'] .social-login {

	.faux-modal__body {
		@include contain-until($b5);
		padding: $space__5;
		padding-bottom: 50px;
	}

	.faux-modal__wrapper {
		max-width: 320px;
	}

	.el-input__inner {
		min-height: 40px;
		padding-left: 1em;
	}

	.button--primary {
		width: 100%;
	}

	.cog-label {
		margin-bottom: 0;
		font-size: $size__3;
		font-weight: $weight__text--normal;
	}

	.template-selection__grid {
		margin-bottom: $space__5;
	}
}
</style>